import React from 'react'
import styled from 'styled-components'

/* Paleta da marca BD:
   - dados de dashboard: Support Blue #2A3B4C (camada complementar)
   - CTA/destaque: BD Digital Red #B0020B (usado com parcimônia)
   - composição multi-série: cores complementares (verde/âmbar/vermelho/cinza) */
export const SERIES = {
  bar: '#2A3B4C', // Support Blue — dados de dashboard
  accent: '#B0020B', // BD Digital Red — CTA/destaque
  ingressos: '#2A3B4C',
  conveniencia: '#4CAF50', // Success Green
  juros: '#FFC107', // Warning Amber
  seguro: '#616161', // Text Secondary
  taxa: '#FF4C3E', // Accent Red
}

/* neutros da marca */
const INK = '#212121' // Text Primary
const MUTED = '#616161' // Text Secondary
const FAINT = '#98A2B3'
const TRACK = '#E0E0E0' // Dividers

const fmtBRL = (n: number) =>
  'R$ ' + (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtInt = (n: number) => Math.round(Number(n) || 0).toLocaleString('pt-BR')
const pct = (v: number, total: number) => (total > 0 ? (v / total) * 100 : 0)
const fmtPct = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'

/* ───────────── Barra empilhada (composição) ───────────── */
const StackTrack = styled.div`
  display: flex;
  width: 100%;
  height: 42px;
  border-radius: 6px;
  overflow: hidden;
  background: ${TRACK};
`
const StackSeg = styled.div<{ $w: number; $c: string }>`
  width: ${(p) => p.$w}%;
  background: ${(p) => p.$c};
  min-width: ${(p) => (p.$w > 0 ? '2px' : '0')};
`
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 12px;
  font-size: 12.5px;
  color: #475467;
`
const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    display: inline-block;
  }
`

export interface StackItem {
  label: string
  value: number
  color: string
}

export const StackedBar: React.FC<{ items: StackItem[]; format?: (n: number) => string }> = ({
  items,
  format = fmtBRL,
}) => {
  const total = items.reduce((s, i) => s + Math.max(0, i.value), 0)
  const visible = items.filter((i) => i.value > 0)
  return (
    <div>
      <StackTrack>
        {visible.map((i) => (
          <StackSeg key={i.label} $w={pct(i.value, total)} $c={i.color} title={`${i.label}: ${format(i.value)}`} />
        ))}
      </StackTrack>
      <Legend>
        {visible.map((i) => (
          <LegendItem key={i.label}>
            <i style={{ background: i.color }} />
            {i.label} — {format(i.value)} ({fmtPct(pct(i.value, total))})
          </LegendItem>
        ))}
      </Legend>
    </div>
  )
}

/* ───────────── Barras horizontais ───────────── */
const HRow = styled.div`
  margin: 12px 0;
  position: relative;
  &:hover .bar-tt {
    opacity: 1;
    visibility: visible;
  }
`
const BarTooltip = styled.div`
  position: absolute;
  z-index: 30;
  left: 0;
  bottom: calc(100% + 6px);
  min-width: 200px;
  background: #101828;
  color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.24);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.1s;
  b {
    font-variant-numeric: tabular-nums;
  }
`
const HTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 5px;
  font-size: 13px;
  color: ${MUTED};
  .v {
    font-weight: 650;
    color: ${INK};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`
const HTrack = styled.div`
  height: 9px;
  background: ${TRACK};
  border-radius: 4px;
  overflow: hidden;
`
const HFill = styled.div<{ $pct: number; $c: string }>`
  height: 100%;
  width: ${(p) => Math.max(1.5, Math.min(100, p.$pct))}%;
  background: ${(p) => p.$c};
  border-radius: 4px;
`
const HSub = styled.div`
  font-size: 11px;
  color: ${FAINT};
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
`

export interface HBarItem {
  label: string
  value: number
  valueLabel: string
  sub?: string
  tooltip?: React.ReactNode
}

export const HBars: React.FC<{ items: HBarItem[]; color?: string }> = ({ items, color = SERIES.bar }) => {
  const max = Math.max(1, ...items.map((i) => i.value))
  if (!items.length) return <Empty>Sem dados no período.</Empty>
  return (
    <div>
      {items.map((i) => (
        <HRow key={i.label}>
          {i.tooltip && <BarTooltip className="bar-tt">{i.tooltip}</BarTooltip>}
          <HTop>
            <span>{i.label}</span>
            <span className="v">{i.valueLabel}</span>
          </HTop>
          <HTrack>
            <HFill $pct={pct(i.value, max)} $c={color} />
          </HTrack>
          {i.sub && <HSub>{i.sub}</HSub>}
        </HRow>
      ))}
    </div>
  )
}

/* ───────────── Barras verticais (semanal) ───────────── */
const WeekWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 200px;
  overflow-x: auto;
  padding-top: 24px;
`
const WeekCol = styled.div`
  flex: 1 0 34px;
  min-width: 34px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
`
const WeekBar = styled.div<{ $h: number }>`
  width: 60%;
  max-width: 34px;
  height: ${(p) => Math.max(2, p.$h)}%;
  background: ${SERIES.bar};
  border-radius: 4px 4px 0 0;
  position: relative;
`
const WeekVal = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10.5px;
  font-weight: 650;
  color: #101828;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`
const WeekLabel = styled.div`
  font-size: 10.5px;
  color: ${FAINT};
  margin-top: 6px;
  white-space: nowrap;
`
const Empty = styled.div`
  color: ${FAINT};
  font-size: 13px;
  padding: 24px 8px;
  text-align: center;
`

/* ───────────── Gráfico de linha (diário/semanal · acumulado) ───────────── */
export interface LinePoint {
  x: string // rótulo do eixo (ex.: DD/MM)
  y: number
  /** dados exibidos no tooltip ao passar o mouse. */
  tip?: { receita: number; ingressos: number; pedidos: number }
  /** linhas livres do tooltip (label + valor formatado) — alternativa genérica ao `tip`. */
  tipLines?: Array<{ label: string; value: string }>
}

const ChartWrap = styled.div`
  position: relative;
`
const LineTip = styled.div<{ $left: number; $top: number }>`
  position: absolute;
  left: ${(p) => p.$left}%;
  top: ${(p) => p.$top}%;
  transform: translate(-50%, -115%);
  background: #212121;
  color: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11.5px;
  line-height: 1.5;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 8px 24px rgba(33, 33, 33, 0.28);
  z-index: 20;
  b {
    font-variant-numeric: tabular-nums;
  }
  .tt-x {
    color: #B0B0B0;
    font-size: 10.5px;
    margin-bottom: 2px;
  }
`

export const LineChart: React.FC<{
  points: LinePoint[]
  format: (n: number) => string
  color?: string
}> = ({ points, format, color = SERIES.bar }) => {
  const [hover, setHover] = React.useState<number | null>(null)

  if (points.length < 2) return <Empty>Dados insuficientes para o gráfico.</Empty>

  const W = 1000
  const H = 280
  const padL = 72
  const padR = 16
  const padT = 14
  const padB = 34
  const iw = W - padL - padR
  const ih = H - padT - padB
  const max = Math.max(1, ...points.map((p) => p.y))
  const xAt = (i: number) => padL + (i / (points.length - 1)) * iw
  const yAt = (v: number) => padT + ih - (v / max) * ih

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p.y).toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${xAt(points.length - 1).toFixed(1)},${padT + ih} L${xAt(0).toFixed(1)},${padT + ih} Z`

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max)
  const labelStep = Math.max(1, Math.ceil(points.length / 8))
  const bandW = iw / (points.length - 1)

  const hp = hover != null ? points[hover] : null

  return (
    <ChartWrap onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Evolução de vendas">
        {ticks.map((t, i) => {
          const gy = yAt(t)
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={gy} y2={gy} stroke={TRACK} strokeWidth={1} />
              <text x={padL - 8} y={gy + 3} fontSize={10.5} fill={FAINT} textAnchor="end">
                {format(t)}
              </text>
            </g>
          )
        })}
        <path d={areaPath} fill={color} opacity={0.08} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {points.map((p, i) =>
          i % labelStep === 0 || i === points.length - 1 ? (
            <text key={i} x={xAt(i)} y={H - 12} fontSize={10} fill={FAINT} textAnchor="middle">
              {p.x}
            </text>
          ) : null
        )}
        {hp && <circle cx={xAt(hover as number)} cy={yAt(hp.y)} r={4} fill={color} />}
        {points.map((p, i) => (
          <rect
            key={`hit-${i}`}
            x={xAt(i) - bandW / 2}
            y={padT}
            width={bandW}
            height={ih + padB}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>
      {hp && (hp.tip || hp.tipLines) && (
        <LineTip $left={(xAt(hover as number) / W) * 100} $top={(yAt(hp.y) / H) * 100}>
          <div className="tt-x">{hp.x}</div>
          {hp.tipLines ? (
            hp.tipLines.map((l) => (
              <div key={l.label}>
                {l.label} <b>{l.value}</b>
              </div>
            ))
          ) : hp.tip ? (
            <>
              <div>
                Receita <b>{fmtBRL(hp.tip.receita)}</b>
              </div>
              <div>
                Ingressos <b>{fmtInt(hp.tip.ingressos)}</b>
              </div>
              <div>
                Pedidos <b>{fmtInt(hp.tip.pedidos)}</b>
              </div>
            </>
          ) : null}
        </LineTip>
      )}
    </ChartWrap>
  )
}

export interface WeekItem {
  label: string
  value: number
  valueLabel: string
  sub?: string
}

export const WeekBars: React.FC<{ items: WeekItem[] }> = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.value))
  if (!items.length) return <Empty>Sem vendas no período.</Empty>
  return (
    <WeekWrap>
      {items.map((i) => (
        <WeekCol key={i.label} title={`${i.label}: ${i.valueLabel}${i.sub ? ` · ${i.sub}` : ''}`}>
          <WeekBar $h={pct(i.value, max)}>
            <WeekVal>{i.valueLabel}</WeekVal>
          </WeekBar>
          <WeekLabel>{i.label}</WeekLabel>
        </WeekCol>
      ))}
    </WeekWrap>
  )
}
